/**
 * EnhancedCreateWODialog — Advanced Work Order Creation Dialog
 * 
 * Features:
 * - Material/Process preview from BOM
 * - Edit material quantities, add/remove/substitute materials
 * - Edit process sequences, add/remove processes, change jobbers
 * - Cost summary with live updates
 * 
 * Used by:
 * - ProductionFulfillmentPage.js (enhanced mode)
 * - FulfillmentDashboardPage.js (enhanced mode)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { 
  Factory, Loader2, Plus, Trash2, ArrowUpDown, ChevronUp, ChevronDown, 
  AlertTriangle, RefreshCw, Package, Settings 
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Props:
 * @param {boolean} open - Dialog open state
 * @param {function} onOpenChange - Dialog open state handler
 * @param {string} itemId - Item ID for WO creation
 * @param {string} itemName - Item name for display
 * @param {string} itemCode - Item code for display (optional)
 * @param {number} defaultQty - Pre-filled WO quantity
 * @param {number} maxQty - Optional max quantity for FNG validation
 * @param {boolean} isSFG - If true, allows exceeding maxQty
 * @param {object} nettStatus - Nett status info {stock_in_hand, so_demand, etc.}
 * @param {function} onSuccess - Callback after successful WO creation
 * @param {ReactNode} children - Optional extra content (FIFO preview)
 */
export default function EnhancedCreateWODialog({
  open,
  onOpenChange,
  itemId,
  itemName,
  itemCode,
  defaultQty,
  maxQty,
  isSFG = false,
  nettStatus,
  onSuccess,
  children
}) {
  // Form state
  const [woQty, setWoQty] = useState('');
  const [woNotes, setWoNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // BOM Preview state
  const [bomData, setBomData] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [hasManualOverrides, setHasManualOverrides] = useState(false);
  
  // Material management
  const [showAddMaterial, setShowAddMaterial] = useState(false);
  const [itemOptions, setItemOptions] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  
  // Process management
  const [showAddProcess, setShowAddProcess] = useState(false);
  const [processOptions, setProcessOptions] = useState([]);
  const [loadingProcesses, setLoadingProcesses] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState([]);

  // Fetch BOM preview when dialog opens
  useEffect(() => {
    if (open && itemId) {
      const qty = defaultQty || 1;
      setWoQty(qty.toString());
      setWoNotes('');
      setHasManualOverrides(false);
      fetchBomPreview(qty);
    }
  }, [open, itemId, defaultQty]);

  const fetchBomPreview = async (qty) => {
    try {
      setLoadingPreview(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/items/${itemId}/bom-preview?wo_qty=${qty}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setBomData(response.data);
      
      // Initialize materials with tracking fields
      const mats = (response.data.materials || []).map((m, idx) => ({
        ...m,
        _key: `bom-${idx}`,
        is_from_bom: true,
        is_substituted: false,
        original_item_id: null,
        _original_qty: m.required_qty
      }));
      setMaterials(mats);
      
      // Initialize processes with tracking fields
      const procs = (response.data.processes || []).map((p, idx) => ({
        ...p,
        _key: `bom-proc-${idx}`,
        is_from_bom: true
      }));
      setProcesses(procs);
      
    } catch (error) {
      console.error('BOM preview error:', error);
      toast.error('Failed to load BOM preview');
      setBomData(null);
      setMaterials([]);
      setProcesses([]);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Handle WO quantity change
  const handleQtyChange = (value) => {
    const newQty = parseFloat(value) || 0;
    setWoQty(value);
    
    if (hasManualOverrides && bomData) {
      // Show confirmation if user has manual overrides
      // For simplicity, just recalculate without confirmation
      recalculateMaterialQuantities(newQty);
    } else if (bomData) {
      recalculateMaterialQuantities(newQty);
    }
  };

  const recalculateMaterialQuantities = (newQty) => {
    setMaterials(prev => prev.map(m => {
      if (m.is_from_bom && m.bom_qty_per_unit) {
        const newReqQty = m.bom_qty_per_unit * newQty;
        return {
          ...m,
          required_qty: newReqQty,
          total_value: newReqQty * (m.unit_price || 0),
          gap: (m.available_stock || 0) - newReqQty
        };
      }
      return m;
    }));
  };

  // Material handlers
  const handleMaterialQtyChange = (key, value) => {
    setMaterials(prev => prev.map(m => {
      if (m._key === key) {
        const numQty = parseFloat(value) || 0;
        setHasManualOverrides(true);
        return {
          ...m,
          required_qty: value,
          total_value: numQty * (m.unit_price || 0),
          gap: (m.available_stock || 0) - numQty
        };
      }
      return m;
    }));
  };

  const handleRemoveMaterial = (key) => {
    const mat = materials.find(m => m._key === key);
    if (mat?.is_from_bom) {
      if (!window.confirm('This material is in the BOM. Removing it means production won\'t receive it automatically. Continue?')) {
        return;
      }
    }
    setMaterials(prev => prev.filter(m => m._key !== key));
    setHasManualOverrides(true);
  };

  const handleAddMaterial = async () => {
    if (!loadingItems && itemOptions.length === 0) {
      await fetchItemOptions();
    }
    setShowAddMaterial(true);
  };

  const fetchItemOptions = async () => {
    try {
      setLoadingItems(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/items?category=RMT&limit=500`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const options = response.data.map(item => ({
        value: item.id,
        label: `${item.item_code} - ${item.name}`,
        code: item.item_code || '',
        category: item.category || '',
        searchTerms: [item.item_code, item.name],
        item
      }));
      setItemOptions(options);
    } catch (error) {
      toast.error('Failed to load items');
    } finally {
      setLoadingItems(false);
    }
  };

  const handleSelectMaterial = (itemId) => {
    const option = itemOptions.find(o => o.value === itemId);
    if (!option) return;
    
    const item = option.item;
    const newMaterial = {
      _key: `added-${Date.now()}`,
      item_id: item.id,
      item_code: item.item_code,
      item_name: item.name,
      unit: item.unit || 'NOS',
      bom_qty_per_unit: 0,
      required_qty: 0,
      available_stock: item.stock_in_hand || 0,
      gap: item.stock_in_hand || 0,
      unit_price: item.purchase_price || item.price || 0,
      total_value: 0,
      is_from_bom: false,
      is_substituted: false,
      original_item_id: null,
      process_index: 0
    };
    
    setMaterials(prev => [...prev, newMaterial]);
    setShowAddMaterial(false);
    setHasManualOverrides(true);
  };

  // Process handlers
  const handleProcessChange = (key, field, value) => {
    setProcesses(prev => prev.map(p => {
      if (p._key === key) {
        setHasManualOverrides(true);
        return { ...p, [field]: value };
      }
      return p;
    }));
  };

  const handleRemoveProcess = (key) => {
    const proc = processes.find(p => p._key === key);
    if (proc?.is_from_bom) {
      if (!window.confirm('This process is in the BOM. Removing it will skip this step. Continue?')) {
        return;
      }
    }
    setProcesses(prev => prev.filter(p => p._key !== key));
    setHasManualOverrides(true);
  };

  const handleMoveProcess = (key, direction) => {
    setProcesses(prev => {
      const idx = prev.findIndex(p => p._key === key);
      if (idx === -1) return prev;
      if (direction === 'up' && idx === 0) return prev;
      if (direction === 'down' && idx === prev.length - 1) return prev;
      
      const newArr = [...prev];
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      [newArr[idx], newArr[swapIdx]] = [newArr[swapIdx], newArr[idx]];
      
      // Update sequences
      return newArr.map((p, i) => ({ ...p, sequence: i + 1 }));
    });
    setHasManualOverrides(true);
  };

  const handleAddProcess = async () => {
    if (!loadingProcesses && processOptions.length === 0) {
      await fetchProcessOptions();
    }
    setShowAddProcess(true);
  };

  const fetchProcessOptions = async () => {
    try {
      setLoadingProcesses(true);
      const token = localStorage.getItem('token');
      const [procRes, suppRes] = await Promise.all([
        axios.get(`${API_URL}/processes?limit=200`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/suppliers?limit=500`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setProcessOptions(procRes.data.map(p => ({
        value: p.id,
        label: p.name,
        process: p
      })));
      
      setSupplierOptions(suppRes.data.map(s => ({
        value: s.id,
        label: s.name
      })));
    } catch (error) {
      toast.error('Failed to load processes');
    } finally {
      setLoadingProcesses(false);
    }
  };

  const handleSelectProcess = (processId) => {
    const option = processOptions.find(o => o.value === processId);
    if (!option) return;
    
    const proc = option.process;
    const newProcess = {
      _key: `added-proc-${Date.now()}`,
      process_id: proc.id,
      process_name: proc.name,
      sequence: processes.length + 1,
      is_outsourced: false,
      jobber_id: null,
      jobber_name: null,
      benchmark_days: proc.benchmark_days || 0,
      is_from_bom: false,
      materials: [],
      checklist: proc.checklist?.map(c => typeof c === 'string' ? c : c.text) || []
    };
    
    setProcesses(prev => [...prev, newProcess]);
    setShowAddProcess(false);
    setHasManualOverrides(true);
  };

  // Calculate cost summary
  const costSummary = React.useMemo(() => {
    const totalValue = materials.reduce((sum, m) => sum + (m.total_value || (m.required_qty * (m.unit_price || 0))), 0);
    const qty = parseFloat(woQty) || 1;
    return {
      total_material_value: totalValue,
      cost_per_unit: totalValue / qty
    };
  }, [materials, woQty]);

  // Submit handler
  const handleCreate = async () => {
    const qty = parseFloat(woQty);
    if (!qty || qty <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }
    if (maxQty && !isSFG && qty > maxQty) {
      toast.error(`Quantity cannot exceed shortage (${maxQty})`);
      return;
    }

    try {
      setCreating(true);
      const token = localStorage.getItem('token');
      
      // Build payload with material/process overrides if modified
      const payload = {
        item_id: itemId,
        wo_qty: qty,
        notes: woNotes
      };
      
      if (hasManualOverrides) {
        // Include materials override
        payload.materials = materials.map(m => ({
          item_id: m.item_id || '',
          item_name: m.item_name,
          item_code: m.item_code || '',
          unit: m.unit || 'NOS',
          bom_qty_per_unit: m.bom_qty_per_unit || 0,
          required_quantity: parseFloat(m.required_qty) || 0,
          unit_price: m.unit_price || 0,
          is_from_bom: m.is_from_bom,
          is_substituted: m.is_substituted || false,
          original_item_id: m.original_item_id || null,
          process_index: m.process_index || 0,
          is_variant: m.is_variant || false,
          variant_group_id: m.variant_group_id || null,
          variant_group_name: m.variant_group_name || '',
          selection_pending: m.selection_pending || false,
          bom_material_index: m.bom_material_index ?? null,
        }));
        
        // Include processes override
        payload.processes = processes.map((p, idx) => ({
          process_id: p.process_id || null,
          process_name: p.process_name,
          sequence: idx + 1,
          is_outsourced: p.is_outsourced || false,
          jobber_id: p.jobber_id || null,
          jobber_name: p.jobber_name || null,
          benchmark_days: parseFloat(p.benchmark_days) || 0,
          is_from_bom: p.is_from_bom,
          checklist: p.checklist || []
        }));
      }
      
      const response = await axios.post(
        `${API_URL}/production-fulfillment/create-wo`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success(`Work Order ${response.data.work_order_number} created for ${response.data.quantity} pcs`);
        if (response.data.allocations?.length > 0) {
          const allocMsg = response.data.allocations.map(a =>
            `${a.so_line_code || a.so_number}: ${a.quantity} pcs`
          ).join(', ');
          toast.info(`FIFO Allocation: ${allocMsg}`, { duration: 5000 });
        }
        if (response.data.warnings?.length > 0) {
          response.data.warnings.forEach(w => toast.warning(w, { duration: 6000 }));
        }
        onOpenChange(false);
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to create Work Order';
      toast.error(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[95vh] flex flex-col" data-testid="enhanced-create-wo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Factory className="h-5 w-5 text-indigo-600" />
            Create Work Order
          </DialogTitle>
          <DialogDescription>
            {itemName}{itemCode ? ` (${itemCode})` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2" style={{ maxHeight: 'calc(95vh - 200px)' }}>
          <div className="space-y-6">
            {/* Status Row */}
            {nettStatus && (
              <div className="flex gap-4 text-sm bg-slate-50 p-3 rounded-lg">
                <div>
                  <span className="text-slate-500">Nett Status:</span>
                  <span className={`ml-1 font-medium ${(nettStatus.nett_status || 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {nettStatus.nett_status || 0}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Available Stock:</span>
                  <span className="ml-1 font-medium">{nettStatus.stock_in_hand || 0}</span>
                </div>
                <div>
                  <span className="text-slate-500">SO Demand:</span>
                  <span className="ml-1 font-medium">{nettStatus.so_blocked || 0}</span>
                </div>
              </div>
            )}

            {/* WO Quantity */}
            <div>
              <label className="text-sm font-medium text-slate-700">
                WO Quantity <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  value={woQty}
                  onChange={(e) => handleQtyChange(e.target.value)}
                  placeholder={maxQty ? `Max: ${maxQty}` : 'Enter quantity'}
                  className="w-40"
                  min={1}
                  data-testid="wo-qty-input"
                />
                {maxQty && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleQtyChange(maxQty.toString())}
                  >
                    Max
                  </Button>
                )}
                {hasManualOverrides && (
                  <Badge variant="outline" className="text-amber-600 border-amber-300">
                    Modified from BOM
                  </Badge>
                )}
              </div>
            </div>

            {/* Materials Section */}
            <div className="border rounded-lg">
              <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b">
                <h3 className="font-medium flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Materials to be Blocked
                  {loadingPreview && <Loader2 className="h-4 w-4 animate-spin" />}
                </h3>
                <Button variant="ghost" size="sm" onClick={handleAddMaterial} data-testid="add-material-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Material
                </Button>
              </div>
              
              {materials.length === 0 ? (
                <div className="p-4 text-center text-slate-500 text-sm">
                  {loadingPreview ? 'Loading materials...' : 'No materials in BOM'}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Material</th>
                        <th className="text-left px-3 py-2 font-medium w-16">Unit</th>
                        <th className="text-right px-3 py-2 font-medium w-20">Per Unit</th>
                        <th className="text-right px-3 py-2 font-medium w-28">Req Qty</th>
                        <th className="text-right px-3 py-2 font-medium w-20">Stock</th>
                        <th className="text-right px-3 py-2 font-medium w-20">Gap</th>
                        <th className="text-right px-3 py-2 font-medium w-24">Value</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {materials.map((mat) => (
                        <tr key={mat._key} className="border-b last:border-0 hover:bg-slate-50">
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{mat.item_name}</span>
                              {mat.is_variant && (
                                <Badge variant="outline" className="text-amber-600">Variant</Badge>
                              )}
                              {!mat.is_from_bom && (
                                <Badge variant="outline" className="text-blue-600">Added</Badge>
                              )}
                              {mat.is_substituted && (
                                <Badge variant="outline" className="text-purple-600">Substituted</Badge>
                              )}
                            </div>
                            <div className="text-xs text-slate-500">{mat.item_code}</div>
                          </td>
                          <td className="px-3 py-2 text-slate-600">{mat.unit}</td>
                          <td className="px-3 py-2 text-right text-slate-600">
                            {mat.bom_qty_per_unit?.toFixed(2) || '-'}
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              value={mat.required_qty || ''}
                              onChange={(e) => handleMaterialQtyChange(mat._key, e.target.value)}
                              className="w-24 h-8 text-right"
                              min={0}
                              step="0.01"
                            />
                          </td>
                          <td className="px-3 py-2 text-right">{mat.available_stock?.toFixed(0) || 0}</td>
                          <td className={`px-3 py-2 text-right font-medium ${(mat.gap || 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {mat.gap?.toFixed(0) || 0}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {((mat.required_qty || 0) * (mat.unit_price || 0)).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                          </td>
                          <td className="px-3 py-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveMaterial(mat._key)}
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Add Material Dialog */}
              {showAddMaterial && (
                <div className="p-4 border-t bg-blue-50">
                  <div className="flex items-center gap-2">
                    <SearchableSelect
                      options={itemOptions}
                      value=""
                      onValueChange={handleSelectMaterial}
                      placeholder="Search and select material..."
                      searchPlaceholder="Search by code or name..."
                      className="flex-1"
                      disabled={loadingItems}
                    />
                    <Button variant="ghost" size="sm" onClick={() => setShowAddMaterial(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Processes Section */}
            <div className="border rounded-lg">
              <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b">
                <h3 className="font-medium flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Production Processes
                </h3>
                <Button variant="ghost" size="sm" onClick={handleAddProcess} data-testid="add-process-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Process
                </Button>
              </div>
              
              {processes.length === 0 ? (
                <div className="p-4 text-center text-slate-500 text-sm">
                  {loadingPreview ? 'Loading processes...' : 'No processes in BOM'}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-center px-2 py-2 font-medium w-10">#</th>
                        <th className="text-left px-3 py-2 font-medium">Process</th>
                        <th className="text-center px-3 py-2 font-medium w-28">Type</th>
                        <th className="text-left px-3 py-2 font-medium w-40">Jobber</th>
                        <th className="text-right px-3 py-2 font-medium w-24">Days</th>
                        <th className="w-20"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {processes.map((proc, idx) => (
                        <tr key={proc._key} className="border-b last:border-0 hover:bg-slate-50">
                          <td className="px-2 py-2 text-center text-slate-500">{idx + 1}</td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{proc.process_name}</span>
                              {!proc.is_from_bom && (
                                <Badge variant="outline" className="text-blue-600">Ad-hoc</Badge>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <span className="text-xs text-slate-500">In-house</span>
                              <Switch
                                checked={proc.is_outsourced}
                                onCheckedChange={(checked) => handleProcessChange(proc._key, 'is_outsourced', checked)}
                              />
                              <span className="text-xs text-slate-500">OJC</span>
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            {proc.is_outsourced ? (
                              <SearchableSelect
                                options={supplierOptions}
                                value={proc.jobber_id || ''}
                                onValueChange={(val) => {
                                  const opt = supplierOptions.find(o => o.value === val);
                                  handleProcessChange(proc._key, 'jobber_id', val);
                                  handleProcessChange(proc._key, 'jobber_name', opt?.label || '');
                                }}
                                placeholder="Select jobber..."
                                className="h-8"
                              />
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <Input
                              type="number"
                              value={proc.benchmark_days || ''}
                              onChange={(e) => handleProcessChange(proc._key, 'benchmark_days', e.target.value)}
                              className="w-20 h-8 text-right"
                              min={0}
                              step="0.5"
                            />
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMoveProcess(proc._key, 'up')}
                                disabled={idx === 0}
                                className="h-7 w-7 p-0"
                              >
                                <ChevronUp className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleMoveProcess(proc._key, 'down')}
                                disabled={idx === processes.length - 1}
                                className="h-7 w-7 p-0"
                              >
                                <ChevronDown className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveProcess(proc._key)}
                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Add Process Dialog */}
              {showAddProcess && (
                <div className="p-4 border-t bg-blue-50">
                  <div className="flex items-center gap-2">
                    <SearchableSelect
                      options={processOptions}
                      value=""
                      onValueChange={handleSelectProcess}
                      placeholder="Search and select process..."
                      searchPlaceholder="Search processes..."
                      className="flex-1"
                      disabled={loadingProcesses}
                    />
                    <Button variant="ghost" size="sm" onClick={() => setShowAddProcess(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Cost Summary */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="font-medium mb-2">Cost Summary</h3>
              <div className="flex gap-8 text-sm">
                <div>
                  <span className="text-slate-500">Total Material Value:</span>
                  <span className="ml-2 font-semibold">
                    {costSummary.total_material_value.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Cost Per Unit:</span>
                  <span className="ml-2 font-semibold">
                    {costSummary.cost_per_unit.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>

            {/* FIFO Preview from parent */}
            {children}

            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-slate-700">Notes (Optional)</label>
              <Textarea
                value={woNotes}
                onChange={(e) => setWoNotes(e.target.value)}
                placeholder="Add notes for this Work Order..."
                className="mt-1"
                rows={2}
                data-testid="wo-notes-input"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={creating || !woQty || parseFloat(woQty) <= 0}
            data-testid="confirm-create-wo-btn"
          >
            {creating ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" />Creating...</>
            ) : (
              <><Factory className="h-4 w-4 mr-2" />Create Work Order</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
